// src/features/system/iam-admin/components/AssignMembershipDialog.tsx
// ────────────────────────────────────────────────────────────────────────────
// Modal for assigning a new membership to the selected user.
// Pure renderer over a controlled form. Submission delegated to parent.
// ────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import AppDialog from '@/shared/components/ui/AppDialog';
import { TEXT } from '@/shared/dsp/cardTokens';
import type {
  TenantPickerEntry,
  AssignMembershipInput,
  MembershipStatus,
} from '../api/iamAdminApi';

export interface AssignMembershipDialogProps {
  open:        boolean;
  onOpenChange: (v: boolean) => void;
  tenants:     TenantPickerEntry[];
  isLoading?:  boolean;
  errorMessage?: string;
  onSubmit:    (input: AssignMembershipInput) => Promise<void> | void;
}

const STATUS_OPTIONS: MembershipStatus[] = ['active', 'inactive', 'locked'];

export default function AssignMembershipDialog({
  open, onOpenChange, tenants, isLoading, errorMessage, onSubmit,
}: AssignMembershipDialogProps) {
  const [tenantRef, setTenantRef] = useState<string>('');
  const [rolesText, setRolesText] = useState('');
  const [status, setStatus]       = useState<MembershipStatus>('active');
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy]           = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Reset when dialog opens/closes
  useEffect(() => {
    if (open) {
      setTenantRef('');
      setRolesText('');
      setStatus('active');
      setIsDefault(false);
      setBusy(false);
      setLocalError(null);
    }
  }, [open]);

  const eligibleTenants = useMemo(
    () => tenants.filter(t => t.idTenant !== 1 && t.isActive !== false),
    [tenants],
  );

  const canSubmit = tenantRef.length > 0 && !busy;

  async function handleConfirm() {
    if (!canSubmit) return;
    setBusy(true);
    setLocalError(null);
    const roles = rolesText
      .split(/[\s,]+/)
      .map(s => s.trim())
      .filter(Boolean);
    try {
      await onSubmit({
        tenant_ref: tenantRef,
        roles,
        status,
        is_default_business: isDefault,
      });
      onOpenChange(false);
    } catch (err: any) {
      setLocalError(err?.detail || err?.message || 'Assignment failed');
      setBusy(false);
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Assign membership"
      description="Grant a user access to a tenant with explicit roles, status and default flag."
      confirmLabel="Assign"
      confirmDisabled={!canSubmit}
      isBusy={busy}
      onConfirm={handleConfirm}
      size="md"
    >
      <div className="space-y-3">
        <label className="block">
          <span className={TEXT.sectionLabel}>Tenant</span>
          <select
            value={tenantRef}
            onChange={e => setTenantRef(e.target.value || '')}
            disabled={isLoading || busy}
            className="pac-glass-input mt-1 w-full rounded px-2 py-1 text-sm text-neutral-200"
            aria-label="Tenant"
          >
            <option value="">— select tenant —</option>
            {eligibleTenants.map(t => (
              <option key={t.tenantRef || t.idTenant} value={t.tenantRef}>
                {t.tenantName} (#{t.idTenant})
              </option>
            ))}
          </select>
          {errorMessage ? (
            <div className="mt-1 text-xs text-red-400">{errorMessage}</div>
          ) : null}
        </label>

        <label className="block">
          <span className={TEXT.sectionLabel}>Roles (comma or space separated)</span>
          <input
            type="text"
            value={rolesText}
            onChange={e => setRolesText(e.target.value)}
            disabled={busy}
            placeholder="operator, viewer"
            className="pac-glass-input mt-1 w-full rounded px-2 py-1 text-sm text-neutral-200"
            aria-label="Roles"
          />
        </label>

        <div className="flex items-center gap-4">
          <label className="flex flex-col">
            <span className={TEXT.sectionLabel}>Status</span>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as MembershipStatus)}
              disabled={busy}
              className="pac-glass-input mt-1 rounded px-2 py-1 text-sm text-neutral-200"
              aria-label="Status"
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-xs text-neutral-300 mt-5">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              disabled={busy}
              aria-label="Mark as default business"
            />
            Mark as default business
          </label>
        </div>

        {localError && (
          <div role="alert" className="text-xs text-red-400">{localError}</div>
        )}
      </div>
    </AppDialog>
  );
}
